import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Flashcards.css';
import { useTranslation } from 'react-i18next';

const Flashcards = () => {
  const { t } = useTranslation();
  const [shuffled, setShuffled] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [decks, setDecks] = useState([]);
  const [selectedDeckId, setSelectedDeckId] = useState('');

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        const res = await axios.get('https://amora-backend.onrender.com/api/student/decks');
        setDecks(res.data);
      } catch (err) {
        console.error('Failed to fetch decks:', err);
      }
    };
    fetchDecks();
  }, []);

  useEffect(() => {
    if (!selectedDeckId) {
      setShuffled([]);
      return;
    }
    const selectedDeck = decks.find(d => d._id === selectedDeckId);
    if (selectedDeck?.events?.length) {
      const shuffledDeck = [...selectedDeck.events].sort(() => 0.5 - Math.random());
      setShuffled(shuffledDeck);
      setCurrentIndex(0);
      setShowBack(false);
      setCompleted(false);
    }
  }, [selectedDeckId, decks]);

  const card = shuffled[currentIndex];

  const handleFlip = () => setShowBack(!showBack);
  const handleNext = () => {
    if (currentIndex + 1 < shuffled.length) {
      setCurrentIndex(prev => prev + 1);
      setShowBack(false);
    } else {
      setCompleted(true);
    }
  };

  return (
    <div className="flex flex-col items-center mt-10 px-4 text-white">
      <div className="max-w-3xl w-full bg-gray-900 p-6 rounded-xl shadow-2xl border border-gray-700">
        <h2 className="text-3xl font-bold mb-6 text-purple-400 text-center">
          {t('flashcards.heading')}
        </h2>

        {/* 🔽 Select Deck */}
        <div className="flex justify-center mb-8">
          <select
            value={selectedDeckId}
            onChange={(e) => setSelectedDeckId(e.target.value)}
            className="bg-gray-800 text-white border border-gray-700 rounded px-4 py-2"
          >
            <option value="">{t('flashcards.chooseDeck')}</option>
            {decks.map(deck => (
              <option key={deck._id} value={deck._id}>{deck.title}</option>
            ))}
          </select>
        </div>

        {/* 🃏 Flashcard */}
        {!selectedDeckId ? (
          <div className="flex flex-col items-center">
            <div className="w-full max-w-md mb-6 p-6 bg-gray-800 border border-gray-700 rounded-xl text-center">
              <p className="text-gray-400 text-lg">{t('flashcards.noDeckSelected')}</p>
            </div>
          </div>
        ) : !completed ? (
          shuffled.length > 0 && (
            <div className="flex flex-col items-center">
              <div className="w-full max-w-md cursor-pointer mb-6" onClick={handleFlip}>
                <div className={`card-wrapper ${showBack ? 'flipped' : ''}`}>
                  <div className="card-face card-front bg-gray-800 border border-gray-700 p-6 rounded-xl text-center">
                    <p className="text-2xl font-semibold text-white">{card.year}</p>
                  </div>
                  <div className="card-face card-back bg-gray-800 border border-gray-700 p-6 rounded-xl text-center">
                    <h3 className="text-xl font-bold text-white mb-2">{card.title}</h3>
                    <p className="text-gray-300 text-sm">{card.description}</p>
                  </div>
                </div>
              </div>

              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow transition"
                onClick={handleNext}
              >
                {t('flashcards.next')}
              </button>

              <p className="mt-4 text-sm text-gray-400">
                {t('flashcards.cardProgress', {
                  current: currentIndex + 1,
                  total: shuffled.length,
                })}
              </p>
            </div>
          )
        ) : (
          <div className="text-center mt-6">
            <p className="text-xl font-semibold text-white mb-2">{t('flashcards.completed')}</p>
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow"
              onClick={() => {
                const reshuffled = [...shuffled].sort(() => 0.5 - Math.random());
                setShuffled(reshuffled);
                setCurrentIndex(0);
                setShowBack(false);
                setCompleted(false);
              }}
            >
              {t('flashcards.restart')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Flashcards;
